import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { BookOpen, Info } from 'lucide-react';
import type { Question, Education } from '../types/question-bank';

interface GuidanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question?: Question;
  education?: Education;
}

export function GuidanceModal({ open, onOpenChange, question, education }: GuidanceModalProps) {
  // Use passed education or fallback to question.education
  const edu = education || question?.education;
  
  if (!edu) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            {question?.text || 'Guidance'}
          </DialogTitle>
          <DialogDescription className="pt-4 space-y-4 text-base component-content">
            <div className="font-medium text-foreground">{edu.content}</div>
            
            {edu.clarification && (
              <div className="p-3 bg-blue-50 text-blue-800 rounded-md text-sm border border-blue-100 flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <strong>Note: </strong> {edu.clarification}
                </div>
              </div>
            )}
            
            {edu.examples && edu.examples.length > 0 && (
              <div className="bg-slate-50 p-3 rounded-md border border-slate-100">
                <div className="text-sm font-semibold mb-2 text-slate-700">Examples:</div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600">
                  {edu.examples.map((ex, i) => (
                    <li key={i}>{ex}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {edu.whyItMatters && (
              <div className="text-sm bg-green-50 p-3 rounded-md border border-green-100 text-green-800">
                <span className="font-semibold">Why it matters: </span>
                {edu.whyItMatters}
              </div>
            )}
            
            {edu.reassurance && (
              <div className="text-sm italic text-muted-foreground pt-2 flex items-center gap-2">
                {edu.reassurance}
              </div>
            )}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}
